import React, { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  Check,
  ArrowLeft,
  Loader2,
  Crown,
  Shield,
  Building2,
  Users,
  Minus,
  Plus,
  UserCircle,
  Mail,
  Clock,
  AlertTriangle,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import {
  useUserDataContext,
  UserData,
  FirmData,
} from "../context/UserDataContext";
import { formatExpirationDate } from "./utils/utils";
import { GetBaseURL } from "./utils/urlUtils";
import { usersApi } from "../services/api";
import CardEntryForm from "./partials/CardEntryForm";
import { billingApi } from "../services/api";

interface UpgradeOptionsProps {
  isOnTrial: boolean;
  setIsOnTrial: React.Dispatch<React.SetStateAction<boolean>>;
}

enum BillingFrequency {
  Monthly = "monthly",
  Annual = "annual",
}

interface PlanTier {
  planNumber: number;
  name: string;
  icon: React.FC<any>;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  popular?: boolean;
}

const PLANS: PlanTier[] = [
  {
    planNumber: 1,
    name: "Starter",
    icon: Shield,
    monthlyPrice: 39,
    annualPrice: 29,
    features: [
      "Unlimited Clients & Matters",
      "Calendar & Task Management",
      "Basic Billing & Invoicing",
      "Document Management",
      "Reports & Dashboard",
    ],
  },
  {
    planNumber: 2,
    name: "Professional",
    icon: Crown,
    monthlyPrice: 69,
    annualPrice: 55,
    features: [
      "Everything in Starter",
      "Messaging & Calls",
      "Leads & Intake Pipeline",
      "E-Signatures",
      "Custom Pipeline Stages",
      "Payment Plans & Autopay",
    ],
    popular: true,
  },
  {
    planNumber: 3,
    name: "Firm",
    icon: Building2,
    monthlyPrice: 99,
    annualPrice: 79,
    features: [
      "Everything in Professional",
      "Immigration Case Management",
      "Real Estate Module",
      "Trust Accounting",
      "Custom Branding",
      "Priority Support",
    ],
  },
];

const UpgradeOptions: React.FC<UpgradeOptionsProps> = ({
  isOnTrial,
  setIsOnTrial,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(
    BillingFrequency.Annual
  );
  const { userData, firmData } = useUserDataContext();
  const [isPurchaseConfirmed, setIsPurchaseConfirmed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Seat management
  const [seatCount, setSeatCount] = useState(1);
  const [teamMembers, setTeamMembers] = useState<
    { _id: string; name: string; email: string; status: string }[]
  >([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [teamFetched, setTeamFetched] = useState(false);

  // Tax info
  const [taxInfo, setTaxInfo] = useState<{
    rates: { name: string; percentage: number }[];
    totalRate: number;
  } | null>(null);

  // Seat reduction modal state
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [removalTarget, setRemovalTarget] = useState(0);
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(
    new Set()
  );
  const [removing, setRemoving] = useState(false);

  // Fetch team members when entering checkout
  const fetchTeamMembers = () => {
    setLoadingTeam(true);
    return usersApi
      .list({ limit: 100 })
      .then((res) => {
        const users = (res.data?.data || res.data || []).filter(
          (u: any) => u.status === "active" || u.status === "invited"
        );
        setTeamMembers(users);
        setTeamFetched(true);
        return users;
      })
      .catch(() => [] as any[])
      .finally(() => setLoadingTeam(false));
  };

  // Fetch team when a plan is selected, and set seat count to match actual team size
  useEffect(() => {
    if (selectedPlan !== null && !teamFetched) {
      fetchTeamMembers().then((users) => {
        setSeatCount(Math.max(1, users.length));
      });
    }
  }, [selectedPlan, teamFetched]);

  // Fetch tax info when entering checkout
  useEffect(() => {
    if (selectedPlan !== null && !taxInfo) {
      billingApi.getTaxInfo()
        .then((res) => setTaxInfo(res.data))
        .catch(() => setTaxInfo({ rates: [], totalRate: 0 }));
    }
  }, [selectedPlan]);

  const stripePublishableKey =
    process.env.NODE_ENV === "production"
      ? `${process.env.REACT_APP_STRIPE_KEY}`
      : `${process.env.REACT_APP_STRIPE_TEST_KEY}`;

  const stripePromise = loadStripe(stripePublishableKey);

  const handlePaymentSuccess = () => {
    setIsPurchaseConfirmed(true);
    setIsOnTrial(false);
  };

  const getSelectedPlanData = (): PlanTier | undefined => {
    return PLANS.find((p) => p.planNumber === selectedPlan);
  };

  const isAnnual = billingFrequency === BillingFrequency.Annual;

  // Removable members: everyone except the current user (owner)
  const removableMembers = teamMembers
    .filter((m) => m._id !== userData._id)
    .sort((a, b) => {
      // Invited first, then active
      if (a.status === "invited" && b.status !== "invited") return -1;
      if (a.status !== "invited" && b.status === "invited") return 1;
      return a.name.localeCompare(b.name);
    });

  const requiredRemovals = teamMembers.length - removalTarget;

  const toggleRemovalSelection = (userId: string) => {
    setSelectedForRemoval((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        if (next.size < requiredRemovals) {
          next.add(userId);
        }
      }
      return next;
    });
  };

  const handleConfirmRemoval = async () => {
    if (selectedForRemoval.size !== requiredRemovals) return;
    setRemoving(true);
    try {
      await usersApi.batchDeactivate(Array.from(selectedForRemoval));
      const updatedUsers = await fetchTeamMembers();
      setSeatCount(removalTarget);
      setShowRemovalModal(false);
      setSelectedForRemoval(new Set());
    } catch (err) {
      // If the batch deactivate fails, keep the modal open so user can retry
      console.error("Failed to deactivate users:", err);
    } finally {
      setRemoving(false);
    }
  };

  const handleCancelRemoval = () => {
    setShowRemovalModal(false);
    setSelectedForRemoval(new Set());
    // Seat count stays as it was before the attempt
  };

  // ─── State 3: Confirmation ──────────────────────────────────────────────────
  if (isPurchaseConfirmed) {
    const planData = getSelectedPlanData();
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col items-center justify-center py-20 px-6 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
          <Check className="h-10 w-10 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">
          Welcome to Kavoa {planData?.name || ""}!
        </h2>
        <p className="text-slate-500 text-lg mb-8">
          Your subscription is active
        </p>
        <button
          onClick={() => (window.location.href = "/")}
          className="px-8 py-3 bg-brand text-white rounded-xl font-bold hover:bg-brand-hover transition-colors"
        >
          Go to Dashboard
        </button>
      </motion.div>
    );
  }

  // ─── State 2: Checkout ──────────────────────────────────────────────────────
  if (selectedPlan !== null) {
    const planData = getSelectedPlanData();
    const PlanIcon = planData?.icon || Shield;
    const pricePerUser = isAnnual
      ? planData?.annualPrice
      : planData?.monthlyPrice;
    const totalMonthly = (pricePerUser || 0) * seatCount;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-3xl mx-auto py-8 px-4"
      >
        {/* Back button */}
        <button
          onClick={() => setSelectedPlan(null)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back to plans</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left column  -  Payment form (3/5) */}
          <div className="lg:col-span-3 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1">Complete your subscription</h2>
              <p className="text-slate-500 text-sm">You'll be charged after your trial ends</p>
            </div>

            {/* Team Seats */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  <h3 className="text-sm font-semibold text-slate-700">Team Seats</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const newCount = seatCount - 1;
                      if (newCount < 1) return;
                      if (newCount < teamMembers.length) {
                        setRemovalTarget(newCount);
                        setSelectedForRemoval(new Set());
                        setShowRemovalModal(true);
                      } else {
                        setSeatCount(newCount);
                      }
                    }}
                    disabled={seatCount <= 1}
                    className="h-9 w-9 rounded-lg border border-slate-300 bg-white flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-lg font-bold text-slate-900 leading-none select-none">&minus;</span>
                  </button>
                  <span className="w-10 text-center text-sm font-bold text-slate-900">{seatCount}</span>
                  <button
                    type="button"
                    onClick={() => setSeatCount((c) => c + 1)}
                    className="h-9 w-9 rounded-lg border border-slate-300 bg-white flex items-center justify-center hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-lg font-bold text-slate-900 leading-none select-none">+</span>
                  </button>
                </div>
              </div>

              {/* Team member list */}
              {loadingTeam ? (
                <div className="flex items-center gap-2 text-slate-400 text-xs py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading team...
                </div>
              ) : teamMembers.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {teamMembers.map((member) => (
                    <div
                      key={member._id}
                      className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg"
                    >
                      <UserCircle className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{member.name}</p>
                        <p className="text-xs text-slate-400 truncate">{member.email}</p>
                      </div>
                      {member.status === "invited" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-semibold rounded-full border border-amber-200">
                          <Clock className="h-2.5 w-2.5" />
                          Invited
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-semibold rounded-full border border-emerald-200">
                          <Check className="h-2.5 w-2.5" />
                          Active
                        </span>
                      )}
                    </div>
                  ))}
                  {seatCount > teamMembers.length && (
                    <div className="flex items-center gap-3 px-3 py-2 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                      <Users className="h-5 w-5 text-slate-300 flex-shrink-0" />
                      <p className="text-xs text-slate-400">
                        +{seatCount - teamMembers.length} additional seat{seatCount - teamMembers.length !== 1 ? "s" : ""} reserved
                      </p>
                    </div>
                  )}
                </div>
              ) : null}

              <p className="text-xs text-slate-400 mt-3">
                Each seat is ${pricePerUser}/mo. You can add or remove seats at any time  -  billing adjusts automatically.
              </p>
            </div>

            {/* Billing frequency toggle */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Billing Cycle</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setBillingFrequency(BillingFrequency.Annual)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                    isAnnual
                      ? "border-brand-accent bg-brand-light"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className={`text-sm font-bold ${isAnnual ? "text-brand" : "text-slate-700"}`}>
                    Annual
                  </span>
                  <span className="text-xs text-emerald-600 font-semibold">Save ~25%</span>
                </button>
                <button
                  type="button"
                  onClick={() => setBillingFrequency(BillingFrequency.Monthly)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                    !isAnnual
                      ? "border-brand-accent bg-brand-light"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className={`text-sm font-bold ${!isAnnual ? "text-brand" : "text-slate-700"}`}>
                    Monthly
                  </span>
                  <span className="text-xs text-slate-400">Flexible</span>
                </button>
              </div>
            </div>

            {/* Card entry */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Payment Method</h3>
              <Elements stripe={stripePromise}>
                <CardEntryForm
                  selectedPlan={selectedPlan}
                  billingFrequency={billingFrequency}
                  onPaymentSuccess={handlePaymentSuccess}
                  seatCount={seatCount}
                />
              </Elements>
            </div>
          </div>

          {/* Right column  -  Order summary (2/5) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-8">
              {/* Plan badge */}
              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-slate-100">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  planData?.popular ? "bg-emerald-100" : "bg-slate-100"
                }`}>
                  <PlanIcon className={`h-5 w-5 ${
                    planData?.popular ? "text-emerald-600" : "text-slate-600"
                  }`} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{planData?.name}</h3>
                  <p className="text-xs text-slate-500">{isAnnual ? "Billed annually" : "Billed monthly"}</p>
                </div>
              </div>

              {/* Line items */}
              <div className="space-y-3 mb-5 pb-5 border-b border-slate-100">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Price per user</span>
                  <span className="text-slate-900 font-medium">${pricePerUser}/mo</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Team members</span>
                  <span className="text-slate-900 font-medium">{seatCount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-900 font-medium">
                    ${isAnnual ? (totalMonthly * 12).toFixed(2) : totalMonthly.toFixed(2)}
                    /{isAnnual ? "yr" : "mo"}
                  </span>
                </div>
                {isAnnual && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Annual discount</span>
                    <span className="text-emerald-600 font-medium">
                      -${((planData?.monthlyPrice || 0) - (planData?.annualPrice || 0)) * seatCount * 12}/yr
                    </span>
                  </div>
                )}
                {/* Tax line items */}
                {taxInfo && taxInfo.rates.length > 0 ? (
                  taxInfo.rates.map((rate, i) => {
                    const baseAmount = isAnnual ? totalMonthly * 12 : totalMonthly;
                    const taxAmount = (baseAmount * rate.percentage) / 100;
                    return (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-slate-500">
                          {rate.name} ({rate.percentage}%)
                        </span>
                        <span className="text-slate-900 font-medium">
                          ${taxAmount.toFixed(2)}/{isAnnual ? "yr" : "mo"}
                        </span>
                      </div>
                    );
                  })
                ) : taxInfo ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tax</span>
                    <span className="text-slate-900 font-medium">$0.00</span>
                  </div>
                ) : null}
              </div>

              {/* Total */}
              {(() => {
                const baseAmount = isAnnual ? totalMonthly * 12 : totalMonthly;
                const taxAmount = taxInfo
                  ? (baseAmount * (taxInfo.totalRate || 0)) / 100
                  : 0;
                const grandTotal = baseAmount + taxAmount;

                return (
                  <div className="flex justify-between items-baseline mb-5">
                    <span className="text-sm font-semibold text-slate-700">Total</span>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">
                        ${grandTotal.toFixed(2)}
                        <span className="text-sm font-normal text-slate-400">
                          /{isAnnual ? "yr" : "mo"}
                        </span>
                      </p>
                      {isAnnual && (
                        <p className="text-xs text-slate-400">
                          ${(grandTotal / 12).toFixed(2)}/mo equivalent
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Trust badges */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <span>Cancel anytime, hassle-free</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <span>30-day money back guarantee</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <span>14-day free trial included</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Seat Reduction Modal */}
        <AnimatePresence>
          {showRemovalModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={handleCancelRemoval}
              />

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.2 }}
                className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
              >
                {/* Modal Header */}
                <div className="p-6 pb-4 border-b border-slate-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Choose team members to remove
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Select {requiredRemovals} team member{requiredRemovals !== 1 ? "s" : ""} to
                        remove to fit {removalTarget} seat{removalTarget !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <button
                      onClick={handleCancelRemoval}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Selection counter */}
                  <div className="mt-3 flex items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${
                        selectedForRemoval.size === requiredRemovals
                          ? "text-emerald-600"
                          : "text-slate-500"
                      }`}
                    >
                      {selectedForRemoval.size} of {requiredRemovals} selected
                    </span>
                    {selectedForRemoval.size === requiredRemovals && (
                      <Check className="h-4 w-4 text-emerald-500" />
                    )}
                  </div>
                </div>

                {/* Member List */}
                <div className="flex-1 overflow-y-auto p-6 pt-3 space-y-2">
                  {removableMembers.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      No removable team members found
                    </div>
                  ) : (
                    removableMembers.map((member) => {
                      const isSelected = selectedForRemoval.has(member._id);
                      const isActive = member.status === "active";
                      const canSelect =
                        isSelected || selectedForRemoval.size < requiredRemovals;

                      return (
                        <button
                          key={member._id}
                          type="button"
                          onClick={() => toggleRemovalSelection(member._id)}
                          disabled={!canSelect && !isSelected}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? "border-red-300 bg-red-50"
                              : canSelect
                              ? "border-slate-200 hover:border-slate-300 bg-white"
                              : "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                          }`}
                        >
                          {/* Checkbox */}
                          <div
                            className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSelected
                                ? "border-red-500 bg-red-500"
                                : "border-slate-300"
                            }`}
                          >
                            {isSelected && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>

                          {/* Avatar initial */}
                          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-slate-600">
                              {(member.name || member.email || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {member.name}
                            </p>
                            <p className="text-xs text-slate-400 truncate">
                              {member.email}
                            </p>
                          </div>

                          {/* Status badge */}
                          {member.status === "invited" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-semibold rounded-full border border-amber-200 flex-shrink-0">
                              <Clock className="h-2.5 w-2.5" />
                              Invited
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-semibold rounded-full border border-emerald-200 flex-shrink-0">
                              <Check className="h-2.5 w-2.5" />
                              Active
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}

                  {/* Warning for active user selection */}
                  {Array.from(selectedForRemoval).some((id) => {
                    const member = teamMembers.find((m) => m._id === id);
                    return member?.status === "active";
                  }) && (
                    <div className="flex items-start gap-2.5 mt-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        This will deactivate an active account. The user will
                        lose access immediately upon confirmation.
                      </p>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="p-6 pt-4 border-t border-slate-100 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleCancelRemoval}
                    disabled={removing}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmRemoval}
                    disabled={
                      selectedForRemoval.size !== requiredRemovals || removing
                    }
                    className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {removing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Removing...
                      </>
                    ) : (
                      <>Confirm & Remove</>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // ─── State 1: Plan Selection ────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-5xl mx-auto py-8 px-4"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">
          Choose Your Plan
        </h2>
        <p className="text-slate-500">
          Start growing your practice with the right tools
        </p>
      </div>

      {/* Monthly / Annual Toggle */}
      <div className="flex items-center justify-center gap-3 mb-10">
        <div className="inline-flex bg-slate-100 rounded-full p-1">
          <button
            onClick={() => setBillingFrequency(BillingFrequency.Monthly)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              billingFrequency === BillingFrequency.Monthly
                ? "bg-brand text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingFrequency(BillingFrequency.Annual)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              billingFrequency === BillingFrequency.Annual
                ? "bg-brand text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Annual
          </button>
        </div>
        {billingFrequency === BillingFrequency.Annual && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex items-center bg-brand-light text-brand text-xs font-semibold px-2.5 py-1 rounded-full"
          >
            Save ~25%
          </motion.span>
        )}
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {PLANS.map((plan, index) => {
          const Icon = plan.icon;
          return (
            <motion.div
              key={plan.planNumber}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              onClick={() => setSelectedPlan(plan.planNumber)}
              className={`relative bg-white rounded-2xl p-8 cursor-pointer transition-all hover:shadow-lg ${
                plan.popular
                  ? "border-2 border-emerald-500 shadow-lg"
                  : "border border-slate-200 hover:border-emerald-300"
              }`}
            >
              {/* Most Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Tier icon + name */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    plan.popular ? "bg-emerald-100" : "bg-slate-100"
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 ${
                      plan.popular ? "text-emerald-600" : "text-slate-600"
                    }`}
                  />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  {plan.name}
                </h3>
              </div>

              {/* Price */}
              <div className="mb-1">
                {isAnnual && (
                  <span className="text-sm text-slate-400 line-through mr-2">
                    ${plan.monthlyPrice}
                  </span>
                )}
                <span className="text-4xl font-bold text-slate-900">
                  ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                </span>
                <span className="text-slate-500 text-sm">/user</span>
                <span className="text-slate-400 text-sm">/mo</span>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                {isAnnual ? "Billed annually" : "Billed monthly"}
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Select button */}
              <button
                onClick={() => setSelectedPlan(plan.planNumber)}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-colors ${
                  plan.popular
                    ? "bg-brand text-white hover:bg-brand-hover"
                    : "bg-slate-100 text-slate-900 hover:bg-slate-200"
                }`}
              >
                Select Plan
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Trial expiration notice */}
      {isOnTrial && firmData?.stripe?.trialExpiration && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-slate-500">
            Your free trial expires on{" "}
            <span className="font-semibold text-slate-700">
              {firmData.stripe.trialExpiration instanceof Date
                ? formatExpirationDate(firmData.stripe.trialExpiration)
                : typeof firmData.stripe.trialExpiration === "string"
                ? formatExpirationDate(
                    new Date(firmData.stripe.trialExpiration)
                  )
                : "N/A"}
            </span>
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default UpgradeOptions;
